import Button from '../components/ui/Button';
import Surface from '../components/ui/Surface';

export const AUDIENCES = [
  { id: 'students', label: 'Students' },
  { id: 'emerging-professionals', label: 'Emerging Professionals' },
  { id: 'busy-families', label: 'Busy Families' },
  { id: 'business-owners', label: 'Business Owners' },
  { id: 'medical-pros', label: 'Medical Professionals' },
  { id: 'first-responders', label: 'Police & Firefighters' },
  { id: 'active-duty-vets', label: 'Active Duty & Veterans' },
  { id: 'near-retirees', label: 'Near-Retirees / Retirees' }
];

export const CONTENT = {
  students: {
    title: 'Students',
    intro:
      'You’re building the foundation for your financial future. Whether you’re still in school or just beginning your career journey, now is the time to set habits that create lifelong confidence with money.',
    howWeHelp: [
      'Budget creation that balances living today while saving for tomorrow.',
      'Smart strategies for managing student loans and building credit early.',
      'Introductory investment education to understand risk, reward, and compounding.',
      'Cash management guidance for part-time income, scholarships, and savings goals.',
      'Tax-aware planning for education credits and deductions (like the American Opportunity Credit).'
    ],
    whyWorkWithUs: [
      'We translate complex financial ideas into clear, actionable steps.',
      'Starting early helps avoid costly mistakes that can follow for decades.',
      'We help you set measurable financial goals and track your progress with accountability.',
      'You’ll learn how to align spending, saving, and investing with your values and career goals.'
    ],
    whatToExpect: [
      'A focused 20-minute call to learn your goals and outline next steps. No pressure—just clarity.',
      'You’ll receive a recap email of our meeting with a secure way to share any relevant information.',
      'We’ll review your situation and identify key actions to build momentum toward your goals.',
      'After your approval, we’ll help you establish a clear budget and starter investment plan.',
      'We’ll meet periodically to track your progress and adjust as your education and career evolve.'
    ],
    cta: { label: 'Start a 20-minute discovery meeting', href: '/contact' },
    ctaLabel: 'Start a 20-minute discovery meeting',
    ctaLink: '/contact'
  },

  'emerging-professionals': {
    title: 'Emerging Professionals',
    intro:
      'You’re building income, choices, and momentum. We help you turn good habits into compounding advantages.',
    help: [
      'Cash-flow design that balances living well today with saving intentionally.',
      'Automated savings across emergency fund, retirement, and opportunity buckets.',
      'Student-loan and debt payoff strategies integrated with investing.',
      'Starter investment policy matched to risk capacity and goals.',
      'Employer plan review (401(k), ESPP, RSUs) and tax-aware choices.',
      'Protection basics (beneficiaries, wills, insurance coordination).'
    ],
    why: [
      'Early decisions compound—small improvements now can be worth six figures later.',
      'Clear rules prevent lifestyle creep from derailing progress.',
      'A written plan builds confidence when markets are noisy.'
    ],
    cta: { label: 'Start a 20-minute discovery meeting', href: '/contact' }
  },

  'busy-families': {
    title: 'Busy Families',
    intro: 'Careers, kids, and competing priorities—your plan should coordinate everything, simply.',
    help: [
      'Family cash-flow and account structure that keeps everyone aligned.',
      'College funding plan with tax-smart use of 529s and custodial accounts.',
      'Insurance & estate basics: guardianship, beneficiaries, and updated documents.',
      'Investment policy that respects risk tolerance and sleep quality.',
      'Tax-aware savings across retirement, HSA, and brokerage accounts.',
      'Annual family finance check-in with action list and accountability.'
    ],
    why: [
      'Complexity grows with every life stage; coordination avoids expensive gaps.',
      'Shared visibility reduces stress and improves follow-through.',
      'A calm process turns ‘someday’ tasks into done items.'
    ],
    cta: { label: 'See how our plan fits your family', href: '/contact' }
  },

  'business-owners': {
    title: 'Business Owners',
    intro: 'Your personal wealth is tied to an enterprise. We align both sides—owner strategy and household planning.',
    help: [
      'Entity structure and retirement plan options (Solo 401(k), SEP, cash balance) modeled to your goals.',
      'Tax-aware cash management: owner draws, quarterly estimates, and reserve targets.',
      'Investment policy that complements business risk—not duplicates it.',
      'Succession, buy-sell and key-person coverage coordination with counsel/CPA.',
      'Exit-readiness scorecard: valuation, timing windows, and after-tax proceeds.',
      'Post-exit portfolio and income design to protect what you built.'
    ],
    why: [
      'Most owner risk lives inside the business; your portfolio should counterbalance it.',
      'Tax and retirement structure can materially change after-tax outcomes.',
      'Exit timing and preparation often create the difference between ‘good’ and ‘great’ results.'
    ],
    cta: { label: 'Get a second opinion on your owner plan', href: '/contact' }
  },

  'medical-pros': {
    title: 'Medical Professionals',
    intro:
      'Irregular schedules, high human-capital value, and complex benefits. We provide a plan that fits the way you work.',
    help: [
      'Student-loan analysis (PAYE/REPAYE/PSLF) and contract review insights.',
      'Benefit selection for group plans, disability protection, and HSAs.',
      'Tax planning for variable income, moonlighting, and practice ownership.',
      'Investment policy tuned to on-call realities and time constraints.',
      'Asset protection and titling coordination with counsel.',
      'Pathways to partnership or private practice, with pro-forma guidance.'
    ],
    why: [
      'Time is scarce—automation and clarity reduce friction.',
      'Benefit choices and loan strategy can be worth tens of thousands over a career.',
      'A coordinated plan avoids blind spots between employer, practice, and personal accounts.'
    ],
    cta: { label: 'Map your plan in one focused call', href: '/contact' }
  },

  'first-responders': {
    title: 'Police & Firefighters',
    slug: 'police-firefighters',
    intro:
      'You protect the community. Your schedule, overtime, and unique benefits deserve a plan that works as hard as you do. Whether you’re early in your career or weighing retirement options, we’ll help translate complex pension rules, pay differentials, and savings choices into clear next steps.',
    howWeHelp: [
      'Pension & survivorship modeling — project income under multiple retirement ages, service years, and COLA scenarios; compare survivor options.',
      'DROP / Deferred Retirement Option evaluation — weigh lump sum vs. monthly pension trade-offs and tax implications.',
      'Benefit coordination — optimize 457(b), 401(a)/403(b), and Roth choices, including age-50 and special 457(b) catch-up opportunities.',
      'Overtime & off-duty income planning — cash-flow strategy for variable pay; tax-aware treatment of details, special duty, and stipends.',
      'Service credit decisions — analyze the cost/benefit of purchasing prior-service or military credit where available.',
      'Risk & insurance review — line-of-duty disability, life insurance, and employer coverage coordination for your family’s protection.',
      'Second-career planning — model pension + new salary, Social Security interactions, and healthcare bridge options.',
      'Tax planning — proactive strategies for withholdings, estimated taxes on off-duty work, HSAs/FSAs, and deductions you actually use.',
      'Estate & beneficiary alignment — make sure pension, 457(b), and insurance beneficiaries match your intentions.'
    ],
    whyWorkWithUs: [
      'Experience with first-responder benefit structures, union contracts, and HR timelines — advice in plain English.',
      'Independent, fiduciary guidance — we sit on your side of the table with transparent pricing.',
      'Scenario planning that reduces uncertainty — side-by-side comparisons you can share with your spouse/partner.',
      'A cadence that fits shift work — quick touchpoints for open enrollment, promotion, and retirement windows.',
      'A written plan with measurement — we track progress and adjust as your career and life evolve.'
    ],
    ctaLabel: 'Start a 20-minute discovery meeting',
    ctaLink: '/contact'
  },

  'active-duty-vets': {
    title: 'Active Duty & Veterans',
    intro:
      'Service changes the way finances work. We coordinate benefits, transitions, and long-term wealth building.',
    help: [
      'Thrift Savings Plan (TSP) and Blended Retirement System optimization.',
      'Housing decisions (BAH/BAS), PCS cash flow, and VA home loan navigation.',
      'SGLI/VGLI and disability considerations with survivor benefits.',
      'Transition planning: translating MOS to civilian comp and benefits.',
      'Tax-aware investing and GI Bill/education benefits planning.',
      'Estate basics: powers, beneficiaries, and portability awareness.'
    ],
    why: [
      'Military benefits are powerful when coordinated—easy to miss, costly to fix later.',
      'Transitions are the riskiest moments; a plan keeps momentum.',
      'Your service deserves a straightforward path to financial independence.'
    ],
    cta: { label: 'Plan your next mission with us', href: '/contact' }
  },

  'near-retirees': {
    title: 'Near-Retirees / Retirees',
    intro: 'You’ve built assets—now convert them into durable, tax-smart income with confidence.',
    help: [
      'Retirement income roadmap (Social Security timing, pensions, annuities).',
      'Tax-aware withdrawal sequencing across accounts (IRA, Roth, taxable).',
      'Portfolio design for reliability: diversification, rebalancing, and risk controls.',
      'Healthcare + Medicare coordination and long-term care planning.',
      'Estate and legacy alignment with attorneys/CPAs; beneficiary audits.',
      'Ongoing monitoring with course corrections as life and markets change.'
    ],
    why: [
      'Order of withdrawals and sequence risk can materially affect lifetime outcomes.',
      'Taxes are one of the few levers you control—planning helps keep more of what you’ve earned.',
      'Clear rules reduce stress so you can enjoy the stage you’ve prepared for.'
    ],
    cta: { label: 'Build your retirement income plan', href: '/contact' }
  }
};

export function AudiencePanel({ id }) {
  const audience = CONTENT[id];
  if (!audience) return null;

  const helpItems = audience.howWeHelp ?? audience.help ?? [];
  const whyItems = audience.whyWorkWithUs ?? audience.why ?? [];
  const defaultSteps = [
    (
      <>
        A focused 20-minute call to learn your goals and outline next steps.
        <span className="italic"> No pressure—just clarity.</span>
      </>
    ),
    'You’ll receive a recap email of our meeting with a secure way to send information and documents.',
    'We review what you currently have and build a plan that aligns with your goals and priorities.',
    'After your approval, we’ll launch your new plan and monitor progress.',
    'We’ll meet throughout the year to review your plan, goals, and life events, adjusting as needed.'
  ];
  const steps = audience.whatToExpect ?? defaultSteps;
  const ctaLabel = audience.cta?.label ?? audience.ctaLabel ?? 'Start a conversation';
  const ctaHref = audience.cta?.href ?? audience.ctaLink ?? '/contact';

  return (
    <div
      id={`panel-${id}`}
      role="tabpanel"
      aria-labelledby={`tab-${id}`}
      className="mt-8 transition-opacity duration-200"
    >
      <Surface className="p-6 md:p-8">
        <div className="grid grid-cols-1 gap-6 md:gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h3 className="text-lg md:text-2xl font-semibold text-slate-900">{audience.title}</h3>
            <p className="mt-2 text-slate-600">{audience.intro}</p>

            <h4 className="mt-6 text-sm font-semibold uppercase tracking-wide text-slate-700">How we help</h4>
            <ul className="mt-2 list-disc space-y-2 pl-5 text-slate-700">
              {helpItems.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <h4 className="mt-6 text-sm font-semibold uppercase tracking-wide text-slate-700">Why work with an advisor like us</h4>
            <ul className="mt-2 list-disc space-y-2 pl-5 text-slate-700">
              {whyItems.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>

            <div className="mt-8">
              <Button
                href={ctaHref}
                variant="primary"
                className="hover:shadow-md hover:-translate-y-0.5"
              >
                {ctaLabel}
              </Button>
            </div>
          </div>

          <Surface className="p-5 bg-slate-50 ring-slate-200/80 flex flex-col justify-between">
            <div>
              <h5 className="text-sm font-semibold text-slate-800 uppercase tracking-wide">
                What to Expect
              </h5>
              <ol className="mt-3 space-y-2 text-sm text-slate-700 list-decimal list-inside">
                {steps.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ol>
            </div>

            <div className="mt-5">
              <Button href="/contact" className="w-full">
                Get Started Today
              </Button>
            </div>
          </Surface>
        </div>
      </Surface>
    </div>
  );
}

